import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";

export const AgentDesktopSubPages = {
    TASKS_PAGE: "tasks",
    CONTACT_PAGE: "contacts",
} as const;

export type SubPagesOptions = (typeof AgentDesktopSubPages)[keyof typeof AgentDesktopSubPages];

export const AgentDesktopMessageChannel = {
    EMAIL: "email",
    SMS: "sms",
} as const;

export type AgentDesktopMessageChannelType =
    (typeof AgentDesktopMessageChannel)[keyof typeof AgentDesktopMessageChannel];

export type ADUIState = {
    subPage: SubPagesOptions;
    contactSearchTerm: string;
    messageChannel: AgentDesktopMessageChannelType | "";
};

const initialState: ADUIState = {
    // the page we are looking at
    subPage: AgentDesktopSubPages.TASKS_PAGE,
    // search term for the contacts panel
    contactSearchTerm: "",
    messageChannel: "",
};

const AgentDesktopUISlice = createSlice({
    name: "agentDesktopUI",
    initialState,
    reducers: {
        setSubPage(state: ADUIState, action: PayloadAction<SubPagesOptions>) {
            state.subPage = action.payload;
        },
        setContactSearchTerm(state: ADUIState, action: PayloadAction<string>) {
            state.contactSearchTerm = action.payload;
        },
        setMessageChannelAction(state: ADUIState, action: PayloadAction<AgentDesktopMessageChannelType>) {
            state.messageChannel = action.payload;
        },
    },
});

export interface Attachment {
    type: string;
    name: string;
    media: Buffer;
    size: number;
    localUrl: string;
}

export const { setSubPage, setContactSearchTerm, setMessageChannelAction } = AgentDesktopUISlice.actions;

export default AgentDesktopUISlice.reducer;

export const AgentDesktopUISliceSelector = (state: RootState): ADUIState => state.agentDesktopUI;

export const selectADSubPage = createSelector(AgentDesktopUISliceSelector, (agentDesktopUI) => agentDesktopUI.subPage);

export const selectADContactSearchTerm = createSelector(
    AgentDesktopUISliceSelector,
    (agentDesktopUI) => agentDesktopUI.contactSearchTerm
);

export const selectIsTasksPage = createSelector(
    selectADSubPage,
    (subPage) => AgentDesktopSubPages.TASKS_PAGE === subPage
);

export const selectIsContactsPage = createSelector(
    selectADSubPage,
    (subPage) => AgentDesktopSubPages.CONTACT_PAGE === subPage
);

export const selectMessageChannel = createSelector(
    AgentDesktopUISliceSelector,
    (agentDesktopUI) => agentDesktopUI.messageChannel
);

// Features
export const shouldShowScheduleButtonOnActivityView = createSelector(
    selectIsContactsPage,
    (isContactsPage) => isContactsPage
);

export const selectShowInfoSidebar = createSelector(selectIsTasksPage, (isTasksPage) => isTasksPage);
